import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, BarChart3, CalendarDays, Search, AlertTriangle, Receipt, DollarSign, Percent, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const localDateTime = (date) => {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const today = new Date();
const defaultStart = localDateTime(new Date(today.getFullYear(), today.getMonth(), 1));
const defaultEnd = localDateTime(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59));
const money = (value) => new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }).format(value || 0);

export default function DataAnalysis() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [store, setStore] = useState(null);
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/stores/${id}`).then(({ data }) => setStore(data)).catch(() => toast.error(t("dataAnalysis.loadStoreFailed")));
  }, [id]);

  const runAnalysis = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get(`/stores/${id}/sales-analysis`, { params: { start, end } });
      setAnalysis(data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await api.get(`/stores/${id}/sales-analysis/export`, {
        params: { start, end },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `data-analysis-${start.slice(0, 10)}-${end.slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || t("dataAnalysis.exportFailed"));
    }
  };

  return (
    <div data-testid="data-analysis-page">
      <Link to={`/stores/${id}/files`} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-2 mb-4">
        <ArrowLeft size={15} /> {t("dataAnalysis.backToFiles")}
      </Link>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-7">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">{t("dataAnalysis.breadcrumb")}</div>
          <h1 className="text-3xl font-extrabold text-slate-900">{store?.name || t("dataAnalysis.storeFallback")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("dataAnalysis.description")}</p>
        </div>
        <div className="pill pill-info"><BarChart3 size={13} /> {t("dataAnalysis.title")}</div>
      </div>

      <form onSubmit={runAnalysis} className="pg-card p-5 mb-6">
        <div className="field-section-title mb-4"><CalendarDays size={16} /> {t("dataAnalysis.interval")}</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
          <label className="field-label">
            {t("dataAnalysis.start")}
            <input className={inputClass} type="datetime-local" required value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className="field-label">
            {t("dataAnalysis.end")}
            <input className={inputClass} type="datetime-local" required value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
          <button type="submit" disabled={loading} className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            <Search size={16} /> {loading ? t("dataAnalysis.analyzing") : t("dataAnalysis.run")}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-3">{t("dataAnalysis.hourlyNote")}</p>
      </form>

      {!analysis ? (
        <div className="pg-card p-12 text-center text-slate-400">{t("dataAnalysis.empty")}</div>
      ) : (
        <>
          <div className={`grid grid-cols-1 ${analysis.has_gst ? "md:grid-cols-4" : "md:grid-cols-2"} gap-4 mb-6`}>
            <Metric icon={DollarSign} label={t("dataAnalysis.totalSales")} value={money(analysis.gross_sales)} tone="cyan" />
            <Metric icon={Receipt} label={t("dataAnalysis.orders")} value={analysis.orders} tone="blue" />
            {analysis.has_gst && <Metric icon={DollarSign} label={t("dataAnalysis.salesExGst")} value={money(analysis.sales_ex_gst)} tone="green" />}
            {analysis.has_gst && <Metric icon={Percent} label="GST" value={money(analysis.gst_amount)} tone="amber" />}
          </div>

          {analysis.missing_sales.length > 0 && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3 text-amber-900">
              <AlertTriangle size={19} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-sm">{t("dataAnalysis.missingSales")}</div>
                <div className="text-sm mt-1">{t("dataAnalysis.missingMessage", { dates: analysis.missing_sales.join(", ") })}</div>
              </div>
            </div>
          )}

          <div className="pg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-bold text-slate-900">{t("dataAnalysis.salesByDay")}</h2>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">{analysis.start} → {analysis.end}</span>
                <button onClick={downloadExcel} className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center gap-2">
                  <Download size={15} /> {t("dataAnalysis.downloadExcel")}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50/70">
                    <th className="px-5 py-3 font-semibold">{t("dataAnalysis.date")}</th>
                    <th className="px-5 py-3 font-semibold">{t("dataAnalysis.orders")}</th>
                    <th className="px-5 py-3 font-semibold">{t("dataAnalysis.totalSales")}</th>
                    {analysis.has_gst && <><th className="px-5 py-3 font-semibold">{t("dataAnalysis.exGst")}</th><th className="px-5 py-3 font-semibold">GST</th></>}
                    <th className="px-5 py-3 font-semibold">{t("common.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.days.map((day) => (
                    <tr key={day.date} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-5 py-3 mono text-slate-700">{day.date}</td>
                      <td className="px-5 py-3">{day.orders}</td>
                      <td className="px-5 py-3 font-medium">{money(day.gross_sales)}</td>
                      {analysis.has_gst && <><td className="px-5 py-3">{money(day.sales_ex_gst)}</td><td className="px-5 py-3">{money(day.gst_amount)}</td></>}
                      <td className="px-5 py-3"><span className={day.missing_sales ? "pill pill-danger" : "pill pill-success"}>{day.missing_sales ? t("dataAnalysis.missingSales") : t("dataAnalysis.reported")}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const inputClass = "mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition bg-white text-sm";

function Metric({ icon: Icon, label, value, tone }) {
  const tones = {
    cyan: "from-cyan-400/15 to-sky-400/10 text-cyan-700 border-cyan-100",
    blue: "from-blue-500/15 to-cyan-500/10 text-blue-700 border-blue-100",
    green: "from-emerald-500/15 to-teal-500/10 text-emerald-700 border-emerald-100",
    amber: "from-amber-500/15 to-orange-500/10 text-amber-700 border-amber-100",
  };
  return <div className={`pg-card p-5 bg-gradient-to-br ${tones[tone]}`}><div className="flex justify-between gap-3"><div><div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 font-semibold">{label}</div><div className="stat-num text-2xl">{value}</div></div><div className="w-10 h-10 rounded-lg bg-white grid place-items-center shadow-sm"><Icon size={18} /></div></div></div>;
}
